import React, { useEffect, useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { LogOut, Users, Mail, Download, ClipboardList, BarChart3, MessageSquareText, Send, Flame, AlertTriangle, CheckCircle2, ChevronDown, ChevronRight, Mailbox, DollarSign } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { getAdminToken, clearAdminToken } from "./AdminLogin";
import { ClickTrackerPanel } from "../components/admin/ClickTrackerPanel";
import { JournalGiftBlastPanel } from "../components/admin/JournalGiftBlastPanel";

const useAuthedApi = () => {
    const token = getAdminToken();
    return {
        get: (path) => api.get(path, { headers: { Authorization: `Bearer ${token}` } }),
        post: (path, body) => api.post(path, body, { headers: { Authorization: `Bearer ${token}` } }),
    };
};

const StatCard = ({ Icon, label, value, testId }) => (
    <div className="bg-black border border-white/15 p-6" data-testid={testId}>
        <div className="flex items-center justify-between">
            <Icon className="text-red-600" size={22} strokeWidth={1.5} />
            <div className="font-display text-4xl">{value ?? "—"}</div>
        </div>
        <div className="mt-3 font-stencil text-xs tracking-widest text-white/50">{label}</div>
    </div>
);

const TableTab = ({ active, onClick, label, count }) => (
    <button
        onClick={onClick}
        className={`px-5 py-3 text-xs font-display tracking-[0.2em] uppercase border-b-2 transition-colors ${
            active ? "border-red-600 text-white" : "border-transparent text-white/50 hover:text-white"
        }`}
        data-testid={`tab-${label.toLowerCase().replace(/\s+/g, '-')}`}
    >
        {label} {count != null && <span className="text-white/40">({count})</span>}
    </button>
);

// Purchase-row expanded detail cell. Optional copy-to-clipboard.
const DetailField = ({ label, value, copy = false, mono = false }) => {
    const display = value || "—";
    const doCopy = (e) => {
        e.stopPropagation();
        if (!value) return;
        navigator.clipboard?.writeText(String(value));
        toast.success(`Copied ${label.toLowerCase()}`);
    };
    return (
        <div>
            <div className="text-[10px] font-stencil tracking-[0.25em] text-white/40 mb-1">{label}</div>
            <div
                className={`${mono ? "font-mono text-xs" : "text-sm"} ${value ? "text-white" : "text-white/30"} ${copy && value ? "cursor-pointer hover:text-red-400" : ""} break-all`}
                onClick={copy ? doCopy : undefined}
                title={copy && value ? "Click to copy" : undefined}
                data-testid={`detail-${label.toLowerCase().replace(/\s+/g, "-")}`}
            >
                {display}
            </div>
        </div>
    );
};

const DripActivity = ({ data, onRetry, retrying, onPurgeTest }) => {
    if (!data) {
        return <div className="py-8 text-white/40 font-stencil text-xs tracking-widest">LOADING DRIP ACTIVITY...</div>;
    }
    const failures = data.failures || [];
    const upcoming = data.upcoming || [];
    const recent = data.recent || [];
    return (
        <div className="py-8 space-y-8" data-testid="tab-content-drip_activity">
            {/* Counters */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-px bg-white/10 border border-white/10">
                <div className="bg-black p-5">
                    <div className="font-stencil text-[10px] tracking-widest text-white/40">SENT</div>
                    <div className="font-display text-3xl text-green-500 mt-1">{data.sent ?? 0}</div>
                </div>
                <div className="bg-black p-5">
                    <div className="font-stencil text-[10px] tracking-widest text-white/40">PENDING</div>
                    <div className="font-display text-3xl text-yellow-500 mt-1">{data.pending ?? 0}</div>
                </div>
                <div className="bg-black p-5">
                    <div className="font-stencil text-[10px] tracking-widest text-white/40">FAILED</div>
                    <div className="font-display text-3xl text-red-600 mt-1">{data.failed ?? 0}</div>
                </div>
                <div className="bg-black p-5">
                    <div className="font-stencil text-[10px] tracking-widest text-white/40">SKIPPED (UNSUB)</div>
                    <div className="font-display text-3xl text-white/60 mt-1">{data.skipped_unsubscribed ?? 0}</div>
                </div>
                <div className="bg-black p-5">
                    <div className="font-stencil text-[10px] tracking-widest text-white/40">TOTAL</div>
                    <div className="font-display text-3xl text-white mt-1">{data.total ?? 0}</div>
                </div>
            </div>

            {/* FAILURES — the diagnostic gold */}
            <div>
                <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
                    <h3 className="font-stencil text-xs tracking-widest text-red-600">[ RECENT FAILURES — WITH EXACT ERROR MESSAGES ]</h3>
                    <div className="flex gap-2 flex-wrap">
                        {failures.length > 0 && (
                            <button
                                onClick={onPurgeTest}
                                className="btn-ghost text-xs"
                                data-testid="drip-purge-test"
                                title="Delete all leftover test/@example.com records from every collection"
                            >
                                🧹 Purge test data
                            </button>
                        )}
                        {failures.length > 0 && (
                            <button
                                onClick={onRetry}
                                disabled={retrying}
                                className="btn-primary text-xs disabled:opacity-50"
                                data-testid="drip-retry-failed"
                            >
                                {retrying ? "Retrying…" : `Retry all ${failures.length} failed`}
                            </button>
                        )}
                    </div>
                </div>
                {failures.length === 0 ? (
                    <div className="text-white/40 py-4 text-sm">No failed sends yet. ✓</div>
                ) : (
                    <div className="border border-red-700/30 bg-[#1a0606] divide-y divide-red-700/20" data-testid="drip-failures-list">
                        {failures.map((f, i) => (
                            <div key={i} className="p-4 text-xs" data-testid={`drip-failure-${i}`}>
                                <div className="flex flex-wrap gap-3 items-start justify-between mb-2">
                                    <div className="text-red-500 font-medium">{f.email}</div>
                                    <div className="text-white/40 font-stencil tracking-widest text-[10px]">
                                        Step #{f.sequence_index} · {new Date(f.failed_at).toLocaleString()}
                                    </div>
                                </div>
                                <div className="text-white/70 mb-2">Subject: {f.subject}</div>
                                <div className="bg-black/40 border border-red-700/30 p-3 font-mono text-[11px] text-yellow-300 break-all">
                                    {f.error || "(no error message captured)"}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Recent successful sends */}
            <div>
                <h3 className="font-stencil text-xs tracking-widest text-green-500 mb-3">[ LAST 10 SUCCESSFUL SENDS ]</h3>
                {recent.length === 0 ? (
                    <div className="text-white/40 py-4 text-sm">No sends yet.</div>
                ) : (
                    <div className="border border-white/10 divide-y divide-white/10">
                        {recent.map((r, i) => (
                            <div key={i} className="p-3 text-xs flex flex-wrap gap-3 items-center justify-between">
                                <div className="text-white/80">{r.email}</div>
                                <div className="text-white/50">Step #{r.sequence_index} — {r.subject}</div>
                                <div className="text-white/40 font-stencil text-[10px] tracking-widest">{new Date(r.sent_at).toLocaleString()}</div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Upcoming */}
            <div>
                <h3 className="font-stencil text-xs tracking-widest text-yellow-500 mb-3">[ NEXT 10 SCHEDULED ]</h3>
                {upcoming.length === 0 ? (
                    <div className="text-white/40 py-4 text-sm">Nothing queued.</div>
                ) : (
                    <div className="border border-white/10 divide-y divide-white/10">
                        {upcoming.map((u, i) => (
                            <div key={i} className="p-3 text-xs flex flex-wrap gap-3 items-center justify-between">
                                <div className="text-white/80">{u.email}</div>
                                <div className="text-white/50">Step #{u.sequence_index} — {u.subject}</div>
                                <div className="text-white/40 font-stencil text-[10px] tracking-widest">{new Date(u.send_at).toLocaleString()}</div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

const DripPreview = ({ data }) => {
    const [selected, setSelected] = useState(0);
    const [blastLoading, setBlastLoading] = useState(false);
    const [blastPreview, setBlastPreview] = useState(null);
    const [journalLoading, setJournalLoading] = useState(false);
    const [journalPreview, setJournalPreview] = useState(null);
    const [augustLoading, setAugustLoading] = useState(false);
    const [augustPreview, setAugustPreview] = useState(null);
    const [holidayLoading, setHolidayLoading] = useState(false);
    const [holidayPreview, setHolidayPreview] = useState(null);
    const [holidayTestEmail, setHolidayTestEmail] = useState("");
    const [holidayTestFirstName, setHolidayTestFirstName] = useState("");
    const [holidayTestIdx, setHolidayTestIdx] = useState(0);
    const [holidayTestLoading, setHolidayTestLoading] = useState(false);
    const authedApi = useAuthedApi();

    const runBlast = async (dryRun) => {
        setBlastLoading(true);
        try {
            const r = await authedApi.post("/admin/sales-drip/backfill", {
                dry_run: dryRun,
                force: true,
            });
            if (dryRun) {
                setBlastPreview(r.data);
                toast.success(`Dry run complete — ${r.data.eligible_for_backfill} leads will get the campaign.`);
            } else {
                toast.success(`July $7 blast fired · ${r.data.enrolled} leads enrolled · ${r.data.total_emails_queued} emails queued`);
                setBlastPreview(null);
            }
        } catch (err) {
            toast.error(err.response?.data?.detail || "Blast failed.");
        } finally {
            setBlastLoading(false);
        }
    };

    const confirmFire = () => {
        if (!blastPreview) return;
        const n = blastPreview.eligible_for_backfill;
        if (!window.confirm(
            `FIRE the July $7 Flash Blast to ${n} leads?\n\n` +
            `• Email #1 fires within 30 seconds.\n` +
            `• Emails #2 and #3 auto-fire on days 5 and 10.\n` +
            `• Buyers and unsubscribes are already excluded.\n\n` +
            `This cannot be undone. Continue?`
        )) return;
        runBlast(false);
    };

    const runJournal = async (dryRun) => {
        setJournalLoading(true);
        try {
            const r = await authedApi.post("/admin/journal-drip/backfill", {
                dry_run: dryRun,
                force: true,
            });
            if (dryRun) {
                setJournalPreview(r.data);
                toast.success(`Dry run complete — ${r.data.eligible_for_backfill} leads will get the journal campaign.`);
            } else {
                toast.success(`Journal blast fired · ${r.data.enrolled} leads · ${r.data.total_emails_queued} emails queued`);
                setJournalPreview(null);
            }
        } catch (err) {
            toast.error(err.response?.data?.detail || "Journal blast failed.");
        } finally {
            setJournalLoading(false);
        }
    };

    const confirmJournalFire = () => {
        if (!journalPreview) return;
        const n = journalPreview.eligible_for_backfill;
        if (!window.confirm(
            `FIRE the Journal Blast to ${n} leads?\n\n` +
            `• 3 emails over 10 days pointing to the MindWorks Daily Battle Plan on Amazon ($12.99).\n` +
            `• Email #1 fires within ~10 seconds.\n` +
            `• Unsubscribes are already excluded.\n\n` +
            `This cannot be undone. Continue?`
        )) return;
        runJournal(false);
    };

    const runAugust = async (dryRun) => {
        setAugustLoading(true);
        try {
            const r = await authedApi.post("/admin/dbd-august/backfill", {
                dry_run: dryRun,
                force: true,
            });
            if (dryRun) {
                setAugustPreview(r.data);
                toast.success(`Dry run complete — ${r.data.eligible_for_backfill} leads will get the August extension.`);
            } else {
                toast.success(`August Extension fired · ${r.data.enrolled} leads · ${r.data.total_emails_queued} emails queued`);
                setAugustPreview(null);
            }
        } catch (err) {
            toast.error(err.response?.data?.detail || "August blast failed.");
        } finally {
            setAugustLoading(false);
        }
    };

    const confirmAugustFire = () => {
        if (!augustPreview) return;
        const n = augustPreview.eligible_for_backfill;
        if (!window.confirm(
            `FIRE the August Extension Blast to ${n} leads?\n\n` +
            `• 4 emails over 13 days announcing the $7 sale is extended through August.\n` +
            `• Email #1 fires within ~10 seconds.\n` +
            `• Existing buyers of The Daily Battle Drill are excluded.\n` +
            `• Unsubscribes are excluded.\n\n` +
            `This cannot be undone. Continue?`
        )) return;
        runAugust(false);
    };

    const runHoliday = async (dryRun) => {
        setHolidayLoading(true);
        try {
            const r = await authedApi.post("/admin/holiday-drip/backfill", {
                dry_run: dryRun,
                force: true,
            });
            if (dryRun) {
                setHolidayPreview(r.data);
                toast.success(`Dry run complete — ${r.data.eligible_for_backfill} leads will get the Holiday series.`);
            } else {
                toast.success(`Holiday series fired · ${r.data.enrolled} leads · ${r.data.total_emails_queued} emails queued`);
                setHolidayPreview(null);
            }
        } catch (err) {
            toast.error(err.response?.data?.detail || "Holiday blast failed.");
        } finally {
            setHolidayLoading(false);
        }
    };

    const confirmHolidayFire = () => {
        if (!holidayPreview) return;
        const n = holidayPreview.eligible_for_backfill;
        if (!window.confirm(
            `FIRE the Holiday Series to ${n} leads?\n\n` +
            `• 10 emails scheduled every Monday from Oct 20 → Dec 22 at 7 AM Central.\n` +
            `• Theme: seasonal depression, taking thoughts captive, year-end pressure, money fear, hope.\n` +
            `• Every email includes a soft Facebook group invite + $7 Battle Drill CTA.\n` +
            `• Course buyers are excluded (no upsell to paying customers).\n` +
            `• Unsubscribes are excluded.\n\n` +
            `This cannot be undone. Continue?`
        )) return;
        runHoliday(false);
    };

    const runHolidayTest = async () => {
        if (!holidayTestEmail || !holidayTestEmail.includes("@")) {
            toast.error("Enter a valid email first.");
            return;
        }
        setHolidayTestLoading(true);
        try {
            const r = await authedApi.post("/admin/holiday-drip/test-send", {
                email: holidayTestEmail,
                first_name: holidayTestFirstName || "",
                sequence_index: Number(holidayTestIdx) || 0,
            });
            toast.success(`Test sent to ${r.data.sent_to} · "${r.data.subject}"`);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Test send failed.");
        } finally {
            setHolidayTestLoading(false);
        }
    };

    if (!data) {
        return <div className="py-8 text-white/40 font-stencil text-xs tracking-widest" data-testid="drip-preview-loading">LOADING DRIP PREVIEW...</div>;
    }
    const emails = data.emails || [];
    const sequences = data.sequences || { welcome: emails, mini_course_sales: [] };
    const current = emails[selected];

    const renderStrip = (list, label, startIdx) => {
        const stripTitle =
            label === "welcome" ? "WELCOME DRIP · 10 EMAILS"
            : label === "journal_blast" ? `JOURNAL BLAST · ${list.length} EMAILS · $12.99`
            : label === "dbd_august_blast" ? `🔥 AUGUST EXTENSION · ${list.length} EMAILS · $7 SALE`
            : label === "holiday_2026" ? `🎄 HOLIDAY SERIES · ${list.length} EMAILS · OCT 20 → DEC 22`
            : `JULY $7 FLASH DRIP · ${list.length} EMAILS`;
        return (
        <div className="mb-8" data-testid={`drip-sequence-${label}`}>
            <p className="text-xs text-white/50 font-stencil tracking-widest mb-3">
                [ {stripTitle} ]
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-5 lg:grid-cols-10 gap-2">
                {list.map((e, i) => {
                    const globalIdx = startIdx + i;
                    return (
                        <button
                            key={`${label}-${i}`}
                            onClick={() => setSelected(globalIdx)}
                            className={`p-3 border text-left transition-colors ${
                                selected === globalIdx
                                    ? "border-red-600 bg-red-700/15"
                                    : "border-white/10 bg-[#0a0a0a] hover:border-white/30"
                            }`}
                            data-testid={`drip-step-${label}-${i}`}
                        >
                            <div className="font-stencil text-[10px] tracking-widest text-red-600">
                                DAY +{e.day_offset}
                            </div>
                            <div className="text-xs text-white/70 mt-1 line-clamp-2 leading-snug">
                                {e.subject}
                            </div>
                        </button>
                    );
                })}
            </div>
        </div>
        );
    };

    return (
        <div className="py-8" data-testid="tab-content-drip_preview">
            {/* ⚡ July $7 Flash Blast — one-click campaign trigger */}
            <div
                className="mb-8 border-2 border-red-700/60 bg-gradient-to-br from-red-950/30 via-black to-black p-6"
                data-testid="july-blast-panel"
            >
                <div className="flex flex-wrap items-start justify-between gap-4">
                    <div className="flex-1 min-w-[280px]">
                        <div className="font-stencil text-[10px] tracking-[0.3em] text-red-500 mb-2">
                            [ CAMPAIGN LAUNCHER ]
                        </div>
                        <h3 className="font-display text-2xl uppercase tracking-tight leading-none mb-2">
                            ⚡ July $7 Flash Blast
                        </h3>
                        <p className="text-sm text-white/65 leading-relaxed max-w-xl">
                            Sends the 3-email $7 July campaign to every lead (newsletter · freebies · quiz) — days 1, 5, 10. Buyers &amp; unsubscribes are excluded. Cancels any prior sales-drip queue so nobody gets the old $27 copy. Personalised with first name.
                        </p>
                    </div>
                    <div className="flex flex-col gap-2">
                        <button
                            onClick={() => runBlast(true)}
                            disabled={blastLoading}
                            className="btn-ghost text-xs"
                            data-testid="july-blast-dryrun"
                        >
                            {blastLoading && !blastPreview ? "Checking…" : "1. Dry-run preview"}
                        </button>
                        <button
                            onClick={confirmFire}
                            disabled={blastLoading || !blastPreview}
                            className="btn-primary text-xs disabled:opacity-40"
                            data-testid="july-blast-fire"
                        >
                            {blastLoading && blastPreview ? "Firing…" : "2. Fire the blast"}
                        </button>
                    </div>
                </div>

                {blastPreview && (
                    <div
                        className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3 border-t border-white/10 pt-5"
                        data-testid="july-blast-preview"
                    >
                        <div>
                            <div className="text-[10px] font-stencil tracking-widest text-white/40">TOTAL LEADS</div>
                            <div className="font-display text-3xl text-white leading-none mt-1">{blastPreview.total_leads_gathered}</div>
                        </div>
                        <div>
                            <div className="text-[10px] font-stencil tracking-widest text-green-500">ELIGIBLE</div>
                            <div className="font-display text-3xl text-green-400 leading-none mt-1">{blastPreview.eligible_for_backfill}</div>
                        </div>
                        <div>
                            <div className="text-[10px] font-stencil tracking-widest text-yellow-500">UNSUB SKIPPED</div>
                            <div className="font-display text-3xl text-yellow-400 leading-none mt-1">{blastPreview.unsubs_excluded}</div>
                        </div>
                        <div>
                            <div className="text-[10px] font-stencil tracking-widest text-red-500">BUYERS SKIPPED</div>
                            <div className="font-display text-3xl text-red-400 leading-none mt-1">{blastPreview.buyers_excluded}</div>
                        </div>
                        <div className="col-span-2 md:col-span-4 text-[11px] text-white/50 mt-2">
                            Ready to fire? Emails will be queued for <span className="text-white font-semibold">{blastPreview.eligible_for_backfill}</span> people. Day 1 fires within 30 seconds. Days 5 &amp; 10 auto-schedule.
                        </div>
                    </div>
                )}
            </div>

            {/* 📖 Journal Blast — one-click campaign trigger for the $12.99 Amazon journal */}
            <div
                className="mb-8 border-2 border-amber-600/50 bg-gradient-to-br from-amber-950/25 via-black to-black p-6"
                data-testid="journal-blast-panel"
            >
                <div className="flex flex-wrap items-start justify-between gap-4">
                    <div className="flex-1 min-w-[280px]">
                        <div className="font-stencil text-[10px] tracking-[0.3em] text-amber-400 mb-2">
                            [ CAMPAIGN LAUNCHER · JOURNAL ]
                        </div>
                        <h3 className="font-display text-2xl uppercase tracking-tight leading-none mb-2">
                            📖 Daily Battle Plan Blast
                        </h3>
                        <p className="text-sm text-white/65 leading-relaxed max-w-xl">
                            3 emails over 10 days pointing to the MindWorks Daily Battle Plan Journal on Amazon ($12.99). Personalised with first name. &ldquo;Engage your life. One day at a time.&rdquo;
                        </p>
                    </div>
                    <div className="flex flex-col gap-2">
                        <button
                            onClick={() => runJournal(true)}
                            disabled={journalLoading}
                            className="btn-ghost text-xs"
                            data-testid="journal-blast-dryrun"
                        >
                            {journalLoading && !journalPreview ? "Checking…" : "1. Dry-run preview"}
                        </button>
                        <button
                            onClick={confirmJournalFire}
                            disabled={journalLoading || !journalPreview}
                            className="text-xs px-4 py-2 disabled:opacity-40 font-display tracking-widest uppercase transition-colors"
                            style={{ background: "rgb(245 158 11)", color: "black" }}
                            data-testid="journal-blast-fire"
                        >
                            {journalLoading && journalPreview ? "Firing…" : "2. Fire the journal blast"}
                        </button>
                    </div>
                </div>

                {journalPreview && (
                    <div
                        className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3 border-t border-white/10 pt-5"
                        data-testid="journal-blast-preview"
                    >
                        <div>
                            <div className="text-[10px] font-stencil tracking-widest text-white/40">TOTAL LEADS</div>
                            <div className="font-display text-3xl text-white leading-none mt-1">{journalPreview.total_leads_gathered}</div>
                        </div>
                        <div>
                            <div className="text-[10px] font-stencil tracking-widest text-green-500">ELIGIBLE</div>
                            <div className="font-display text-3xl text-green-400 leading-none mt-1">{journalPreview.eligible_for_backfill}</div>
                        </div>
                        <div>
                            <div className="text-[10px] font-stencil tracking-widest text-yellow-500">UNSUB SKIPPED</div>
                            <div className="font-display text-3xl text-yellow-400 leading-none mt-1">{journalPreview.unsubs_excluded}</div>
                        </div>
                        <div>
                            <div className="text-[10px] font-stencil tracking-widest text-white/40">ALREADY ENROLLED</div>
                            <div className="font-display text-3xl text-white/70 leading-none mt-1">{journalPreview.already_enrolled_excluded}</div>
                        </div>
                        <div className="col-span-2 md:col-span-4 text-[11px] text-white/50 mt-2">
                            Ready to fire? Emails will be queued for <span className="text-white font-semibold">{journalPreview.eligible_for_backfill}</span> people. Day 1 fires within ~10 seconds. Days 5 &amp; 10 auto-schedule.
                        </div>
                    </div>
                )}
            </div>

            {/* 🔥 August Extension Blast — one-click campaign for the $7 sale extension */}
            <div
                className="mb-8 border-2 border-orange-600/60 bg-gradient-to-br from-orange-950/30 via-black to-black p-6"
                data-testid="august-blast-panel"
            >
                <div className="flex flex-wrap items-start justify-between gap-4">
                    <div className="flex-1 min-w-[280px]">
                        <div className="font-stencil text-[10px] tracking-[0.3em] text-orange-400 mb-2">
                            [ CAMPAIGN LAUNCHER · AUGUST EXTENSION ]
                        </div>
                        <h3 className="font-display text-2xl uppercase tracking-tight leading-none mb-2">
                            🔥 August Extension Blast
                        </h3>
                        <p className="text-sm text-white/65 leading-relaxed max-w-xl">
                            4 emails over 13 days announcing the $7 Daily Battle Drill sale is extended through August 31st. &ldquo;If you didn&apos;t get a chance in July, here&apos;s your chance.&rdquo; Existing buyers &amp; unsubs are auto-excluded. Personalised with first name.
                        </p>
                    </div>
                    <div className="flex flex-col gap-2">
                        <button
                            onClick={() => runAugust(true)}
                            disabled={augustLoading}
                            className="btn-ghost text-xs"
                            data-testid="august-blast-dryrun"
                        >
                            {augustLoading && !augustPreview ? "Checking…" : "1. Dry-run preview"}
                        </button>
                        <button
                            onClick={confirmAugustFire}
                            disabled={augustLoading || !augustPreview}
                            className="text-xs px-4 py-2 disabled:opacity-40 font-display tracking-widest uppercase transition-colors"
                            style={{ background: "rgb(234 88 12)", color: "white" }}
                            data-testid="august-blast-fire"
                        >
                            {augustLoading && augustPreview ? "Firing…" : "2. Fire the August blast"}
                        </button>
                    </div>
                </div>

                {augustPreview && (
                    <div
                        className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3 border-t border-white/10 pt-5"
                        data-testid="august-blast-preview"
                    >
                        <div>
                            <div className="text-[10px] font-stencil tracking-widest text-white/40">TOTAL LEADS</div>
                            <div className="font-display text-3xl text-white leading-none mt-1">{augustPreview.total_leads_gathered}</div>
                        </div>
                        <div>
                            <div className="text-[10px] font-stencil tracking-widest text-green-500">ELIGIBLE</div>
                            <div className="font-display text-3xl text-green-400 leading-none mt-1">{augustPreview.eligible_for_backfill}</div>
                        </div>
                        <div>
                            <div className="text-[10px] font-stencil tracking-widest text-yellow-500">UNSUB SKIPPED</div>
                            <div className="font-display text-3xl text-yellow-400 leading-none mt-1">{augustPreview.unsubs_excluded}</div>
                        </div>
                        <div>
                            <div className="text-[10px] font-stencil tracking-widest text-red-500">BUYERS SKIPPED</div>
                            <div className="font-display text-3xl text-red-400 leading-none mt-1">{augustPreview.buyers_excluded}</div>
                        </div>
                        <div className="col-span-2 md:col-span-4 text-[11px] text-white/50 mt-2">
                            Ready to fire? Emails will be queued for <span className="text-white font-semibold">{augustPreview.eligible_for_backfill}</span> people. Day 1 fires within ~10 seconds. Days 4, 8 &amp; 13 auto-schedule.
                        </div>
                    </div>
                )}
            </div>

            {/* 🎄 Holiday Series — 10-email Mon-weekly sequence Oct 20 → Dec 22 */}
            <div
                className="mb-8 border-2 border-emerald-600/60 bg-gradient-to-br from-emerald-950/30 via-black to-black p-6"
                data-testid="holiday-blast-panel"
            >
                <div className="flex flex-wrap items-start justify-between gap-4">
                    <div className="flex-1 min-w-[280px]">
                        <div className="font-stencil text-[10px] tracking-[0.3em] text-emerald-400 mb-2">
                            [ CAMPAIGN LAUNCHER · HOLIDAY SERIES ]
                        </div>
                        <h3 className="font-display text-2xl uppercase tracking-tight leading-none mb-2">
                            🎄 Holiday Series — 10 Mondays through Christmas
                        </h3>
                        <p className="text-sm text-white/65 leading-relaxed max-w-2xl">
                            10 raw-truth emails every Monday 7 AM Central (Oct 20 → Dec 22). Themes: seasonal depression, taking thoughts captive (2 Cor 10:5), year-end pressure, comparison, Thanksgiving, Advent, money fear, year-end reflection, Christmas week. Every email includes a soft Facebook group invite + $7 Battle Drill CTA. Course buyers &amp; unsubs auto-excluded.
                        </p>
                    </div>
                    <div className="flex flex-col gap-2">
                        <button
                            onClick={() => runHoliday(true)}
                            disabled={holidayLoading}
                            className="btn-ghost text-xs"
                            data-testid="holiday-blast-dryrun"
                        >
                            {holidayLoading && !holidayPreview ? "Checking…" : "1. Dry-run preview"}
                        </button>
                        <button
                            onClick={confirmHolidayFire}
                            disabled={holidayLoading || !holidayPreview}
                            className="text-xs px-4 py-2 disabled:opacity-40 font-display tracking-widest uppercase transition-colors"
                            style={{ background: "rgb(16 185 129)", color: "black" }}
                            data-testid="holiday-blast-fire"
                        >
                            {holidayLoading && holidayPreview ? "Firing…" : "2. Fire the Holiday series"}
                        </button>
                    </div>
                </div>

                {holidayPreview && (
                    <div
                        className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3 border-t border-white/10 pt-5"
                        data-testid="holiday-blast-preview"
                    >
                        <div>
                            <div className="text-[10px] font-stencil tracking-widest text-white/40">TOTAL LEADS</div>
                            <div className="font-display text-3xl text-white leading-none mt-1">{holidayPreview.total_leads_gathered}</div>
                        </div>
                        <div>
                            <div className="text-[10px] font-stencil tracking-widest text-green-500">ELIGIBLE</div>
                            <div className="font-display text-3xl text-green-400 leading-none mt-1">{holidayPreview.eligible_for_backfill}</div>
                        </div>
                        <div>
                            <div className="text-[10px] font-stencil tracking-widest text-yellow-500">UNSUB SKIPPED</div>
                            <div className="font-display text-3xl text-yellow-400 leading-none mt-1">{holidayPreview.unsubs_excluded}</div>
                        </div>
                        <div>
                            <div className="text-[10px] font-stencil tracking-widest text-red-500">BUYERS SKIPPED</div>
                            <div className="font-display text-3xl text-red-400 leading-none mt-1">{holidayPreview.buyers_excluded}</div>
                        </div>
                        <div className="col-span-2 md:col-span-4 text-[11px] text-white/50 mt-2">
                            Ready to fire? 10 emails × {holidayPreview.eligible_for_backfill} people = <span className="text-white font-semibold">{holidayPreview.eligible_for_backfill * 10}</span> emails queued. First send: <span className="text-white">Mon Oct 20 · 7 AM CT</span>. Last: <span className="text-white">Mon Dec 22 · 7 AM CT</span>.
                        </div>
                    </div>
                )}

                {/* Test-send row */}
                <div className="mt-6 border-t border-white/10 pt-5">
                    <div className="font-stencil text-[10px] tracking-[0.3em] text-emerald-400 mb-3">
                        [ LIVE TEST — SEND ONE EMAIL TO ANY ADDRESS NOW ]
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                        <input
                            type="email"
                            placeholder="your@email.com"
                            value={holidayTestEmail}
                            onChange={(e) => setHolidayTestEmail(e.target.value)}
                            className="col-span-1 md:col-span-2 bg-black border border-white/20 text-white text-sm px-3 py-2 focus:outline-none focus:border-emerald-500"
                            data-testid="holiday-test-email"
                        />
                        <input
                            type="text"
                            placeholder="First name (optional)"
                            value={holidayTestFirstName}
                            onChange={(e) => setHolidayTestFirstName(e.target.value)}
                            className="bg-black border border-white/20 text-white text-sm px-3 py-2 focus:outline-none focus:border-emerald-500"
                            data-testid="holiday-test-firstname"
                        />
                        <select
                            value={holidayTestIdx}
                            onChange={(e) => setHolidayTestIdx(e.target.value)}
                            className="bg-black border border-white/20 text-white text-sm px-3 py-2 focus:outline-none focus:border-emerald-500"
                            data-testid="holiday-test-idx"
                        >
                            {(sequences.holiday_2026 || []).map((e, i) => (
                                <option key={i} value={i}>
                                    #{i + 1} — {e.subject.slice(0, 50)}
                                </option>
                            ))}
                        </select>
                    </div>
                    <button
                        onClick={runHolidayTest}
                        disabled={holidayTestLoading || !holidayTestEmail}
                        className="mt-3 text-xs px-4 py-2 disabled:opacity-40 font-display tracking-widest uppercase transition-colors border border-emerald-500 text-emerald-400 hover:bg-emerald-500 hover:text-black"
                        data-testid="holiday-test-send"
                    >
                        {holidayTestLoading ? "Sending…" : "📧 Send test email now"}
                    </button>
                    <p className="text-[11px] text-white/40 mt-2">
                        Fires one real email to the address above using Resend. Check your inbox + spam. No one else gets it.
                    </p>
                </div>
            </div>

            <JournalGiftBlastPanel authedApi={authedApi} />
            <ClickTrackerPanel authedApi={authedApi} />

            <p className="text-xs text-white/50 font-stencil tracking-widest mb-6">
                [ ALL DRIP EMAILS · RENDERED WITH &quot;TIM&quot; AS FIRST NAME · CLICK A STEP TO PREVIEW ]
            </p>
            {renderStrip(sequences.welcome, "welcome", 0)}
            {sequences.mini_course_sales?.length > 0 && renderStrip(sequences.mini_course_sales, "mini_course_sales", sequences.welcome.length)}
            {sequences.journal_blast?.length > 0 && renderStrip(sequences.journal_blast, "journal_blast", sequences.welcome.length + (sequences.mini_course_sales?.length || 0))}
            {sequences.dbd_august_blast?.length > 0 && renderStrip(sequences.dbd_august_blast, "dbd_august_blast", sequences.welcome.length + (sequences.mini_course_sales?.length || 0) + (sequences.journal_blast?.length || 0))}
            {sequences.holiday_2026?.length > 0 && renderStrip(sequences.holiday_2026, "holiday_2026", sequences.welcome.length + (sequences.mini_course_sales?.length || 0) + (sequences.journal_blast?.length || 0) + (sequences.dbd_august_blast?.length || 0))}

            {current && (
                <div className="border border-white/10 bg-[#0a0a0a]" data-testid="drip-preview-current">
                    <div className="border-b border-white/10 px-6 py-4 flex flex-wrap items-center justify-between gap-3 bg-black">
                        <div>
                            <div className="font-stencil text-[10px] tracking-widest text-red-600">
                                {current.sequence === "mini_course_sales" ? "MINI-COURSE SALES" : "WELCOME"} · DAY +{current.day_offset}
                            </div>
                            <div className="font-display text-lg mt-1">{current.subject}</div>
                        </div>
                        <div className="text-[10px] font-stencil tracking-widest text-white/40">
                            FROM: hello@mail.wearemindworks.org
                        </div>
                    </div>
                    <iframe
                        srcDoc={current.html}
                        title={`Drip email ${current.index}`}
                        className="w-full bg-white"
                        style={{ height: "720px", border: 0 }}
                        sandbox="allow-same-origin"
                        data-testid="drip-preview-iframe"
                    />
                </div>
            )}
        </div>
    );
};

const AdminDashboard = () => {
    const navigate = useNavigate();
    const token = getAdminToken();
    const authedApi = useAuthedApi();
    const [stats, setStats] = useState(null);
    const [tab, setTab] = useState("contacts");
    const [data, setData] = useState({ contacts: null, newsletter: null, freebies: null, quiz: null, checkins: null, broadcasts: null, drip_preview: null, drip_activity: null, course_waitlist: null, speaking: null, students: null, email_events: null, purchases: null });
    const [openPurchase, setOpenPurchase] = useState(null); // which purchase row is expanded
    const [grantForm, setGrantForm] = useState({ email: "", course_id: "daily-battle-drill", send_email: true, note: "" });
    const [granting, setGranting] = useState(false);
    const [broadcast, setBroadcast] = useState({ subject: "", body_text: "", audience: "all" });
    const [broadcasting, setBroadcasting] = useState(false);
    const [showBroadcast, setShowBroadcast] = useState(false);
    const [preview, setPreview] = useState(null);
    const [expandedJob, setExpandedJob] = useState(null);
    const [showDripSubscribe, setShowDripSubscribe] = useState(false);
    const [dripSub, setDripSub] = useState({ email: "", first_name: "", last_name: "" });
    const [dripSubLoading, setDripSubLoading] = useState(false);
    const [retryingDrip, setRetryingDrip] = useState(false);
    const [courseMode, setCourseMode] = useState(null);
    const [togglingMode, setTogglingMode] = useState(false);

    useEffect(() => {
        // Fetch current course mode for the toggle
        authedApi.get("/course").then((r) => setCourseMode(r.data?.mode || "waitlist")).catch(() => {});
    }, []);

    const submitGrantAccess = async (e) => {
        e.preventDefault();
        if (!grantForm.email) return;
        const target = grantForm.email.trim().toLowerCase();
        const courseName = grantForm.course_id === "daily-battle-drill" ? "The Daily Battle Drill ($7)" : "MindWorks BattleFront ($97)";
        if (!window.confirm(`Grant access to ${courseName} for ${target}?${grantForm.send_email ? " A confirmation email will be sent." : ""}`)) return;
        setGranting(true);
        try {
            await authedApi.post("/admin/grant-access", {
                email: target,
                course_id: grantForm.course_id,
                send_email: grantForm.send_email,
                note: grantForm.note.trim(),
            });
            toast.success(`Access granted to ${target}.${grantForm.send_email ? " Email queued." : ""}`);
            setGrantForm((f) => ({ ...f, email: "", note: "" }));
            setData((d) => ({ ...d, students: null }));
            const r = await authedApi.get("/admin/students");
            setData((d) => ({ ...d, students: r.data }));
        } catch (err) {
            toast.error(err.response?.data?.detail || "Grant failed.");
        } finally {
            setGranting(false);
        }
    };


    const toggleCourseMode = async () => {
        if (!courseMode) return;
        const next = courseMode === "live" ? "waitlist" : "live";
        const warn = next === "live"
            ? "Switch the course to LIVE? The enroll button will start taking $97 payments via Stripe."
            : "Switch the course back to WAITLIST mode? The enroll button will collect names only — no payments.";
        if (!window.confirm(warn)) return;
        setTogglingMode(true);
        try {
            const r = await authedApi.post("/admin/course/mode", { mode: next });
            setCourseMode(r.data.mode);
            toast.success(`Course mode: ${r.data.mode.toUpperCase()}`);
        } catch (e) {
            toast.error("Failed to flip mode.");
        } finally {
            setTogglingMode(false);
        }
    };

    const retryFailedDrips = async () => {
        if (!window.confirm("Re-attempt every failed drip send? This will reset failed entries to pending and immediately retry the Day-0 welcomes.")) return;
        setRetryingDrip(true);
        try {
            const r = await authedApi.post("/admin/drip-retry-failed", {});
            toast.success(`Reset ${r.data.reset_count} entries · ${r.data.fired_now} Day-0 emails firing now`);
            // Reload drip activity data
            const fresh = await authedApi.get("/admin/drip-stats");
            setData((d) => ({ ...d, drip_activity: fresh.data }));
        } catch (err) {
            toast.error("Retry failed.");
        } finally {
            setRetryingDrip(false);
        }
    };

    const purgeTestData = async () => {
        if (!window.confirm("Delete ALL test / @example.com / @test.* records from every collection (drip_schedule, newsletter, freebies, quiz, contacts, purchases, students, etc.)?\n\nThis clears the fake test data that was polluting your dashboard. It only removes emails matching test patterns — real leads are safe.\n\nContinue?")) return;
        try {
            const r = await authedApi.post("/admin/purge-test-data", {});
            const detail = Object.entries(r.data.by_collection || {}).filter(([, n]) => n > 0).map(([k, n]) => `${k}: ${n}`).join(" · ");
            toast.success(`Removed ${r.data.total_removed} test records — ${detail || "nothing found"}`);
            const fresh = await authedApi.get("/admin/drip-stats");
            setData((d) => ({ ...d, drip_activity: fresh.data }));
        } catch (err) {
            toast.error(err.response?.data?.detail || "Purge failed.");
        }
    };

    useEffect(() => {
        if (!token) return;
        loadStats();
        loadTab("contacts");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadStats = async () => {
        try {
            const r = await authedApi.get("/admin/stats");
            setStats(r.data);
        } catch (err) {
            if (err.response?.status === 401) handleLogout();
        }
    };

    const loadTab = async (which) => {
        setTab(which);
        if (data[which]) return;
        try {
            // drip_preview maps to a different endpoint shape than the table tabs
            let endpoint;
            if (which === "drip_preview") endpoint = "/admin/drip-preview?first_name=Tim";
            else if (which === "drip_activity") endpoint = "/admin/drip-stats";
            else if (which === "course_waitlist") endpoint = "/admin/course/waitlist";
            else if (which === "speaking") endpoint = "/admin/speaking-requests";
            else if (which === "students") endpoint = "/admin/students";
            else endpoint = `/admin/${which}`;
            const r = await authedApi.get(endpoint);
            setData((d) => ({ ...d, [which]: r.data }));
        } catch (err) {
            if (err.response?.status === 401) handleLogout();
        }
    };

    const handleLogout = () => {
        clearAdminToken();
        navigate("/admin/login");
    };

    const sendBroadcast = async (e) => {
        e.preventDefault();
        if (!window.confirm(`Send "${broadcast.subject}" to ${preview?.deliverable ?? "all"} subscribers?`)) return;
        setBroadcasting(true);
        try {
            const r = await authedApi.post("/admin/broadcast", broadcast);
            toast.success(`Dispatching to ${r.data.recipients} subscribers. Job: ${r.data.job_id?.slice(0, 8)}.`);
            setBroadcast({ subject: "", body_text: "", audience: "all" });
            setShowBroadcast(false);
            setPreview(null);
            // Refresh broadcasts tab data so the new job appears
            setData((d) => ({ ...d, broadcasts: null }));
        } catch (err) {
            toast.error("Broadcast failed.");
        } finally {
            setBroadcasting(false);
        }
    };

    const refreshBroadcasts = async () => {
        try {
            const r = await authedApi.get("/admin/broadcasts");
            setData((d) => ({ ...d, broadcasts: r.data }));
        } catch (err) {
            if (err.response?.status === 401) handleLogout();
        }
    };

    const submitDripSubscribe = async (e) => {
        e.preventDefault();
        if (!dripSub.email || !dripSub.first_name.trim()) return;
        setDripSubLoading(true);
        try {
            const r = await authedApi.post("/admin/drip-subscribe", {
                email: dripSub.email,
                first_name: dripSub.first_name.trim(),
                last_name: dripSub.last_name.trim(),
                fire_day_zero_now: true,
            });
            const d = r.data;
            const msg = d.had_existing_entries > 0
                ? `Already enrolled — re-fired Day 0 to ${d.email}`
                : `Subscribed ${d.email} · Day 0 dispatched · ${d.scheduled_entries}/${d.total_steps} emails queued`;
            toast.success(msg);
            setDripSub({ email: "", first_name: "", last_name: "" });
            setShowDripSubscribe(false);
            // Refresh stats so the Drip card updates
            loadStats();
        } catch (err) {
            toast.error(err.response?.data?.detail?.[0]?.msg || "Failed to subscribe to drip.");
        } finally {
            setDripSubLoading(false);
        }
    };

    const loadPreview = async () => {
        try {
            const r = await authedApi.get(`/admin/broadcast/preview?audience=${broadcast.audience || "all"}`);
            setPreview(r.data);
        } catch (err) {
            toast.error("Could not load recipient preview.");
        }
    };

    const toggleBroadcast = () => {
        const next = !showBroadcast;
        setShowBroadcast(next);
        if (next) loadPreview();
    };

    // Refresh preview count when audience changes
    useEffect(() => {
        if (showBroadcast) loadPreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [broadcast.audience]);

    if (!token) return <Navigate to="/admin/login" replace />;

    const rows = data[tab] || [];

    return (
        <div className="bg-black min-h-screen" data-testid="admin-dashboard">
            {/* Header */}
            <div className="border-b border-white/10 py-8">
                <div className="max-w-7xl mx-auto px-6 lg:px-10 flex flex-wrap items-end justify-between gap-4">
                    <div>
                        <div className="font-stencil tracking-widest text-red-600 text-xs">[ COMMAND CONSOLE ]</div>
                        <h1 className="mt-2 font-display text-4xl md:text-5xl uppercase tracking-tighter">
                            Mission Control
                        </h1>
                    </div>
                    <div className="flex gap-3 flex-wrap">
                        {courseMode && (
                            <button
                                onClick={toggleCourseMode}
                                disabled={togglingMode}
                                className={`btn-ghost text-xs disabled:opacity-50 ${courseMode === "live" ? "border-green-500/50 text-green-400 hover:bg-green-700/10" : "border-yellow-500/40 text-yellow-400 hover:bg-yellow-700/10"}`}
                                style={{ borderWidth: 1, borderStyle: "solid" }}
                                data-testid="admin-course-mode-toggle"
                            >
                                COURSE: {togglingMode ? "..." : courseMode.toUpperCase()}
                            </button>
                        )}
                        <button
                            onClick={() => setShowDripSubscribe(true)}
                            className="btn-ghost text-xs"
                            data-testid="drip-subscribe-toggle"
                        >
                            <Mailbox size={14} /> Add to Drip
                        </button>
                        <button
                            onClick={toggleBroadcast}
                            className="btn-ghost text-xs"
                            data-testid="broadcast-toggle"
                        >
                            <Send size={14} /> Broadcast
                        </button>
                        <button onClick={handleLogout} className="btn-ghost text-xs" data-testid="admin-logout">
                            <LogOut size={14} /> Sign Out
                        </button>
                    </div>
                </div>
            </div>

            {/* Stats */}
            <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-9 gap-px bg-white/10 border border-white/10">
                    <StatCard Icon={Mail} label="Newsletter" value={stats?.newsletter} testId="stat-newsletter" />
                    <StatCard Icon={Download} label="Freebie Claims" value={stats?.freebie_claims} testId="stat-freebies" />
                    <StatCard Icon={ClipboardList} label="Contacts" value={stats?.contacts} testId="stat-contacts" />
                    <StatCard Icon={BarChart3} label="Quiz Submissions" value={stats?.quiz_submissions} testId="stat-quiz" />
                    <StatCard Icon={Flame} label="Check-in Users" value={stats?.checkin_sessions} testId="stat-checkins" />
                    <StatCard Icon={Users} label="Chat Sessions" value={stats?.chat_sessions} testId="stat-sessions" />
                    <StatCard Icon={MessageSquareText} label="Chat Messages" value={stats?.chat_messages} testId="stat-messages" />
                    <StatCard Icon={Mailbox} label="Drip · Sent / Queued" value={`${stats?.drip_sent ?? 0}/${stats?.drip_pending ?? 0}`} testId="stat-drip" />
                    <StatCard Icon={DollarSign} label="Revenue · Buyers" value={`$${(stats?.revenue_usd ?? 0).toLocaleString(undefined, {minimumFractionDigits: 0, maximumFractionDigits: 0})} · ${stats?.purchases_paid ?? 0}`} testId="stat-revenue" />
                </div>
            </div>

            {/* Broadcast form */}
            {showBroadcast && (
                <div className="max-w-7xl mx-auto px-6 lg:px-10 pb-6">
                    <form onSubmit={sendBroadcast} className="border border-red-700 bg-[#0a0a0a] p-8" data-testid="broadcast-form">
                        <div className="font-stencil text-xs tracking-widest text-red-600 mb-4">
                            [ BROADCAST TO ALL SUBSCRIBERS ]
                        </div>

                        {/* Recipient preview */}
                        {preview ? (
                            <div className="mb-6 border border-white/10 bg-black p-5" data-testid="broadcast-preview">
                                <div className="flex items-end justify-between flex-wrap gap-4">
                                    <div>
                                        <div className="font-stencil text-[10px] tracking-widest text-white/40">DELIVERABLE</div>
                                        <div className="font-display text-5xl text-red-600">{preview.deliverable}</div>
                                        <div className="mt-1 text-xs text-white/50">
                                            unique recipients across all sources
                                            {preview.unsubscribed > 0 && (
                                                <span className="ml-2 text-white/40">· {preview.unsubscribed} unsubscribed excluded</span>
                                            )}
                                        </div>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={loadPreview}
                                        className="text-[10px] font-stencil tracking-widest text-white/40 hover:text-red-600 transition-colors"
                                    >
                                        ↻ REFRESH
                                    </button>
                                </div>
                                <div className="mt-4 grid grid-cols-2 sm:grid-cols-5 gap-px bg-white/10 border border-white/10">
                                    {Object.entries(preview.breakdown).map(([source, count]) => (
                                        <div key={source} className="bg-black px-3 py-2">
                                            <div className="font-stencil text-[10px] tracking-widest text-red-600">{source.toUpperCase()}</div>
                                            <div className="font-display text-2xl">{count}</div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ) : (
                            <div className="mb-6 text-xs text-white/40 font-stencil tracking-widest animate-pulse">LOADING RECIPIENTS...</div>
                        )}

                        <div className="mb-4">
                            <label className="block text-xs font-stencil tracking-widest text-red-600 mb-2">SEND TO</label>
                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="broadcast-audience">
                                {[
                                    { v: "all", label: "Everyone" },
                                    { v: "freebies", label: "Freebie Subs" },
                                    { v: "newsletter", label: "Newsletter" },
                                    { v: "quiz", label: "Assessment" },
                                    { v: "contacts", label: "Contacts" },
                                    { v: "checkins", label: "Check-ins" },
                                ].map((opt) => (
                                    <button
                                        type="button"
                                        key={opt.v}
                                        onClick={() => setBroadcast({ ...broadcast, audience: opt.v })}
                                        className={`px-3 py-2 text-xs font-display tracking-tight uppercase border ${broadcast.audience === opt.v ? "bg-red-700 border-red-700 text-white" : "bg-black border-white/15 text-white/70 hover:border-red-600/50"}`}
                                        data-testid={`broadcast-audience-${opt.v}`}
                                    >
                                        {opt.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <input
                            placeholder="Subject line"
                            value={broadcast.subject}
                            onChange={(e) => setBroadcast({ ...broadcast, subject: e.target.value })}
                            required
                            className="w-full px-4 py-3 bg-black border border-white/15 focus:border-red-600 outline-none text-white mb-3"
                            data-testid="broadcast-subject"
                        />
                        <textarea
                            placeholder="Your message — use {first_name} anywhere to personalize. Line breaks preserved."
                            value={broadcast.body_text}
                            onChange={(e) => setBroadcast({ ...broadcast, body_text: e.target.value })}
                            required
                            rows={8}
                            className="w-full px-4 py-3 bg-black border border-white/15 focus:border-red-600 outline-none text-white resize-none"
                            data-testid="broadcast-body"
                        />
                        <p className="mt-2 text-xs text-white/40">
                            Tip: type <code className="text-red-500">{"{first_name}"}</code> anywhere in the body and it will be replaced with each recipient's first name (or "friend" if not available).
                        </p>
                        <button type="submit" disabled={broadcasting || !preview?.deliverable} className="mt-4 btn-primary disabled:opacity-50" data-testid="broadcast-send">
                            <Send size={14} /> {broadcasting ? "Sending..." : `Send to ${preview?.deliverable ?? "—"}`}
                        </button>
                    </form>
                </div>
            )}

            {/* Drip subscribe form */}
            {showDripSubscribe && (
                <div className="max-w-7xl mx-auto px-6 lg:px-10 pb-6">
                    <form onSubmit={submitDripSubscribe} className="border border-red-700 bg-[#0a0a0a] p-8" data-testid="drip-subscribe-form">
                        <div className="font-stencil text-xs tracking-widest text-red-600 mb-4">
                            [ ADD SOMEONE TO TIM'S 10-EMAIL WELCOME DRIP ]
                        </div>
                        <p className="text-white/60 text-sm mb-4 max-w-2xl">
                            Day 0 (welcome) fires immediately. The remaining 9 emails roll out every 3 days for the next 27 days.
                            Re-subscribing the same email is a no-op (no duplicates).
                        </p>
                        <div className="grid md:grid-cols-3 gap-3">
                            <input
                                type="text"
                                placeholder="First name"
                                value={dripSub.first_name}
                                onChange={(e) => setDripSub({ ...dripSub, first_name: e.target.value })}
                                required
                                className="px-4 py-3 bg-black border border-white/15 focus:border-red-600 outline-none text-white placeholder-white/30"
                                data-testid="drip-sub-first-name"
                                autoComplete="given-name"
                            />
                            <input
                                type="text"
                                placeholder="Last name (optional)"
                                value={dripSub.last_name}
                                onChange={(e) => setDripSub({ ...dripSub, last_name: e.target.value })}
                                className="px-4 py-3 bg-black border border-white/15 focus:border-red-600 outline-none text-white placeholder-white/30"
                                data-testid="drip-sub-last-name"
                                autoComplete="family-name"
                            />
                            <input
                                type="email"
                                placeholder="email@address.com"
                                value={dripSub.email}
                                onChange={(e) => setDripSub({ ...dripSub, email: e.target.value })}
                                required
                                className="px-4 py-3 bg-black border border-white/15 focus:border-red-600 outline-none text-white placeholder-white/30"
                                data-testid="drip-sub-email"
                                autoComplete="email"
                            />
                        </div>
                        <div className="flex gap-3 mt-4">
                            <button type="submit" disabled={dripSubLoading} className="btn-primary disabled:opacity-50" data-testid="drip-sub-submit">
                                <Mailbox size={14} /> {dripSubLoading ? "Subscribing..." : "Start their drip now"}
                            </button>
                            <button type="button" onClick={() => setShowDripSubscribe(false)} className="btn-ghost text-xs" data-testid="drip-sub-cancel">
                                Cancel
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {/* Tabs */}
            <div className="max-w-7xl mx-auto px-6 lg:px-10">
                <div className="border-b border-white/10 flex flex-wrap">
                    <TableTab active={tab === "contacts"} onClick={() => loadTab("contacts")} label="Contacts" count={stats?.contacts} />
                    <TableTab active={tab === "newsletter"} onClick={() => loadTab("newsletter")} label="Newsletter" count={stats?.newsletter} />
                    <TableTab active={tab === "freebies"} onClick={() => loadTab("freebies")} label="Freebies" count={stats?.freebie_claims} />
                    <TableTab active={tab === "quiz"} onClick={() => loadTab("quiz")} label="Assessment" count={stats?.quiz_submissions} />
                    <TableTab active={tab === "checkins"} onClick={() => loadTab("checkins")} label="Checkins" count={stats?.checkin_sessions} />
                    <TableTab active={tab === "broadcasts"} onClick={() => loadTab("broadcasts")} label="Broadcasts" />
                    <TableTab active={tab === "drip_preview"} onClick={() => loadTab("drip_preview")} label="Drip Preview" />
                    <TableTab active={tab === "drip_activity"} onClick={() => loadTab("drip_activity")} label="Drip Activity" />
                    <TableTab active={tab === "course_waitlist"} onClick={() => loadTab("course_waitlist")} label="Waitlist" />
                    <TableTab active={tab === "speaking"} onClick={() => loadTab("speaking")} label="Speaking" count={data.speaking?.length} />
                    <TableTab active={tab === "students"} onClick={() => loadTab("students")} label="Access" count={data.students?.length} />
                    <TableTab active={tab === "email_events"} onClick={() => loadTab("email_events")} label="Email Tracking" count={data.email_events?.events?.length} />
                    <TableTab active={tab === "purchases"} onClick={() => loadTab("purchases")} label="Purchases" count={data.purchases?.count} />
                </div>

                {/* Speaking requests view */}
                {tab === "speaking" && (
                    <div className="py-8" data-testid="tab-content-speaking">
                        <p className="text-xs text-white/50 font-stencil tracking-widest mb-6">
                            [ SPEAKING ENGAGEMENT REQUESTS · NEWEST FIRST ]
                        </p>
                        {!data.speaking && <div className="text-white/40 font-stencil text-xs tracking-widest">LOADING...</div>}
                        {data.speaking && data.speaking.length === 0 && (
                            <div className="text-white/40 py-10 text-center font-stencil text-xs tracking-widest">— No speaking requests yet —</div>
                        )}
                        {data.speaking && data.speaking.length > 0 && (
                            <div className="space-y-3" data-testid="speaking-rows">
                                {data.speaking.map((r) => (
                                    <div key={r.id} className="border border-white/10 bg-[#0a0a0a] p-5" data-testid={`speaking-row-${r.id}`}>
                                        <div className="flex flex-wrap items-baseline gap-4 mb-2">
                                            <div className="font-display text-lg">{r.first_name} {r.last_name}</div>
                                            <div className="text-sm text-red-500">{r.organization}</div>
                                            <div className="ml-auto text-xs font-stencil tracking-widest text-white/40">{new Date(r.created_at).toLocaleString()}</div>
                                        </div>
                                        <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1 text-sm text-white/70 mb-2">
                                            <div><span className="text-white/40">Email:</span> <a href={`mailto:${r.email}`} className="hover:text-red-500">{r.email}</a></div>
                                            <div><span className="text-white/40">Phone:</span> <a href={`tel:${r.phone}`} className="hover:text-red-500">{r.phone}</a></div>
                                            {r.event_type && <div><span className="text-white/40">Event:</span> {r.event_type}</div>}
                                            {r.event_date && <div><span className="text-white/40">Date:</span> {r.event_date}</div>}
                                        </div>
                                        {r.message && (
                                            <div className="mt-3 border-l-2 border-red-700/50 pl-4 py-1 text-sm text-white/75 italic leading-relaxed">
                                                "{r.message}"
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {/* Access (manual unlock) view */}
                {tab === "students" && (
                    <div className="py-8" data-testid="tab-content-students">
                        <p className="text-xs text-white/50 font-stencil tracking-widest mb-6">
                            [ MANUAL COURSE ACCESS · USE WHEN STRIPE FAILS OR FOR COMPS ]
                        </p>
                        <form onSubmit={submitGrantAccess} className="border border-white/10 bg-[#0a0a0a] p-6 mb-8 grid sm:grid-cols-2 gap-4" data-testid="grant-access-form">
                            <div className="sm:col-span-2 font-display text-xl uppercase tracking-tight">Grant Access</div>
                            <input
                                type="email"
                                required
                                placeholder="buyer@example.com"
                                value={grantForm.email}
                                onChange={(e) => setGrantForm((f) => ({ ...f, email: e.target.value }))}
                                className="px-4 py-3 bg-black border border-white/15 focus:border-red-600 outline-none text-white placeholder-white/30"
                                data-testid="grant-email"
                            />
                            <select
                                value={grantForm.course_id}
                                onChange={(e) => setGrantForm((f) => ({ ...f, course_id: e.target.value }))}
                                className="px-4 py-3 bg-black border border-white/15 focus:border-red-600 outline-none text-white"
                                data-testid="grant-course"
                            >
                                <option value="daily-battle-drill">The Daily Battle Drill — $7</option>
                                <option value="battlefield-course">MindWorks BattleFront — $97</option>
                            </select>
                            <input
                                type="text"
                                placeholder="Internal note (optional) — e.g., 'Stripe 502, refunded via PayPal'"
                                value={grantForm.note}
                                onChange={(e) => setGrantForm((f) => ({ ...f, note: e.target.value }))}
                                className="sm:col-span-2 px-4 py-3 bg-black border border-white/15 focus:border-red-600 outline-none text-white placeholder-white/30"
                                data-testid="grant-note"
                            />
                            <label className="flex items-center gap-2 text-sm text-white/75">
                                <input
                                    type="checkbox"
                                    checked={grantForm.send_email}
                                    onChange={(e) => setGrantForm((f) => ({ ...f, send_email: e.target.checked }))}
                                    data-testid="grant-send-email"
                                />
                                Send confirmation/setup email
                            </label>
                            <button
                                type="submit"
                                disabled={granting}
                                className="btn-primary justify-center disabled:opacity-50"
                                data-testid="grant-submit"
                            >
                                {granting ? "Granting..." : "Grant Access"}
                            </button>
                        </form>

                        <p className="text-xs text-white/50 font-stencil tracking-widest mb-4">
                            [ ALL ENROLLED STUDENTS · NEWEST FIRST ]
                        </p>
                        {!data.students && <div className="text-white/40 font-stencil text-xs tracking-widest">LOADING...</div>}
                        {data.students && data.students.length === 0 && (
                            <div className="text-white/40 py-10 text-center font-stencil text-xs tracking-widest">— No students yet —</div>
                        )}
                        {data.students && data.students.length > 0 && (
                            <div className="overflow-x-auto border border-white/10" data-testid="students-table">
                                <table className="w-full text-sm">
                                    <thead className="bg-[#0a0a0a] text-white/45 text-xs font-stencil tracking-widest">
                                        <tr>
                                            <th className="px-4 py-3 text-left">EMAIL</th>
                                            <th className="px-4 py-3 text-left">COURSES</th>
                                            <th className="px-4 py-3 text-left">PASSWORD SET</th>
                                            <th className="px-4 py-3 text-left">CREATED</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data.students.map((s) => (
                                            <tr key={s.email} className="border-t border-white/5" data-testid={`student-row-${s.email}`}>
                                                <td className="px-4 py-3">{s.email}</td>
                                                <td className="px-4 py-3 text-white/75">{(s.courses || []).join(", ") || "—"}</td>
                                                <td className="px-4 py-3">{s.has_password ? <span className="text-emerald-500">YES</span> : <span className="text-white/45">NO</span>}</td>
                                                <td className="px-4 py-3 text-white/55">{s.created_at ? new Date(s.created_at).toLocaleString() : "—"}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                )}

                {/* Email tracking view */}
                {tab === "email_events" && (() => {
                    const e = data.email_events;
                    if (!e) return <div className="py-8 text-white/40 font-stencil text-xs tracking-widest">LOADING...</div>;
                    const c = e.counts || {};
                    const Tile = ({ label, value, color }) => (
                        <div className="border border-white/10 bg-[#0a0a0a] p-5" data-testid={`email-tile-${label.toLowerCase()}`}>
                            <div className={`text-3xl font-display ${color || "text-white"}`}>{value || 0}</div>
                            <div className="text-[10px] font-stencil tracking-[0.25em] text-white/45 mt-1">{label}</div>
                        </div>
                    );
                    return (
                        <div className="py-8" data-testid="tab-content-email_events">
                            <p className="text-xs text-white/50 font-stencil tracking-widest mb-6">
                                [ EMAIL TRACKING · LIVE EVENTS FROM RESEND ]
                            </p>
                            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-8">
                                <Tile label="SENT" value={c.sent} color="text-white/80" />
                                <Tile label="DELIVERED" value={c.delivered} color="text-emerald-500" />
                                <Tile label="UNIQUE OPENS" value={e.unique_opens} color="text-blue-400" />
                                <Tile label="UNIQUE CLICKS" value={e.unique_clicks} color="text-red-500" />
                                <Tile label="BOUNCED" value={c.bounced} color="text-amber-500" />
                                <Tile label="COMPLAINED" value={c.complained} color="text-rose-500" />
                            </div>
                            {(!e.events || e.events.length === 0) ? (
                                <div className="border border-white/10 bg-[#0a0a0a] p-8 text-center" data-testid="email-tracking-empty">
                                    <p className="text-white/60 mb-2">No events yet.</p>
                                    <p className="text-xs text-white/45 leading-relaxed max-w-md mx-auto">
                                        To start tracking, set up the Resend webhook (see the Help section in the docs / I&apos;ll walk you through it) and enable open + click tracking on your domain.
                                    </p>
                                </div>
                            ) : (
                                <div className="overflow-x-auto border border-white/10" data-testid="email-events-table">
                                    <table className="w-full text-sm">
                                        <thead className="bg-[#0a0a0a] text-white/45 text-xs font-stencil tracking-widest">
                                            <tr>
                                                <th className="px-4 py-3 text-left">WHEN</th>
                                                <th className="px-4 py-3 text-left">EVENT</th>
                                                <th className="px-4 py-3 text-left">RECIPIENT</th>
                                                <th className="px-4 py-3 text-left">SUBJECT</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {e.events.map((row, i) => {
                                                const color = row.type === "opened" ? "text-blue-400"
                                                    : row.type === "clicked" ? "text-red-500"
                                                    : row.type === "delivered" ? "text-emerald-500"
                                                    : row.type === "sent" ? "text-white/70"
                                                    : row.type === "bounced" ? "text-amber-500"
                                                    : row.type === "complained" ? "text-rose-500"
                                                    : "text-white/55";
                                                const icon = row.type === "opened" ? "👁"
                                                    : row.type === "clicked" ? "🖱"
                                                    : row.type === "delivered" ? "📬"
                                                    : row.type === "sent" ? "📤"
                                                    : row.type === "bounced" ? "⚠"
                                                    : row.type === "complained" ? "🛑"
                                                    : "•";
                                                return (
                                                    <tr key={i} className="border-t border-white/5">
                                                        <td className="px-4 py-3 text-white/55 whitespace-nowrap">{row.created_at ? new Date(row.created_at).toLocaleString() : "—"}</td>
                                                        <td className={`px-4 py-3 font-stencil tracking-widest text-xs ${color}`}>{icon} {(row.type || "?").toUpperCase()}</td>
                                                        <td className="px-4 py-3">{row.to || "—"}</td>
                                                        <td className="px-4 py-3 text-white/65">{row.subject || "—"}</td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    );
                })()}


                {/* Purchases (paid course transactions) */}
                {tab === "purchases" && (() => {
                    const p = data.purchases;
                    if (!p) return <div className="py-8 text-white/40 font-stencil text-xs tracking-widest">LOADING...</div>;
                    const rows = p.rows || [];
                    const runEnrich = async () => {
                        if (!window.confirm("Pull buyer email/name/phone/address from Stripe for every purchase that's missing details?\n\nThis calls the Stripe API once per stale transaction — safe to run anytime.")) return;
                        try {
                            const r = await authedApi.post("/admin/purchases/enrich", {});
                            toast.success(`Enriched ${r.data.enriched} · Skipped ${r.data.skipped} · Errored ${r.data.errored}`);
                            // Refresh the table
                            const fresh = await authedApi.get("/admin/purchases");
                            setData((d) => ({ ...d, purchases: fresh.data }));
                        } catch (err) {
                            toast.error(err.response?.data?.detail || "Enrich failed.");
                        }
                    };
                    const sendTestAlert = async () => {
                        try {
                            const r = await authedApi.post("/admin/purchases/test-owner-alert", {});
                            toast.success(`Test alert sent to ${r.data.to}. Check your inbox in ~10 sec.`);
                        } catch (err) {
                            toast.error(err.response?.data?.detail || "Test alert failed.");
                        }
                    };
                    const syncStripe = async () => {
                        if (!window.confirm("Scan the last 30 days of Stripe checkout sessions and reconcile them into your dashboard?\n\nThis catches any purchases that slipped through (e.g. buyer never hit the success page). Idempotent — safe to run anytime.")) return;
                        try {
                            toast.info("Scanning Stripe… this may take 10-30 seconds.");
                            const r = await authedApi.post("/admin/purchases/sync-stripe", {});
                            toast.success(`Synced ${r.data.reconciled} sessions · ${r.data.newly_paid} newly-paid recovered · ${r.data.inserted} new rows`);
                            // Refresh the table
                            const fresh = await authedApi.get("/admin/purchases");
                            setData((d) => ({ ...d, purchases: fresh.data }));
                        } catch (err) {
                            toast.error(err.response?.data?.detail || "Sync failed.");
                        }
                    };
                    return (
                        <div className="py-8" data-testid="tab-content-purchases">
                            {/* Top summary tiles + Enrich button */}
                            <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 flex-1">
                                    <div className="border border-emerald-700/40 bg-emerald-950/20 p-5" data-testid="purchase-tile-revenue">
                                        <div className="text-3xl font-display text-emerald-400">${(p.total_revenue_usd ?? 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</div>
                                        <div className="text-[10px] font-stencil tracking-[0.25em] text-white/45 mt-1">TOTAL REVENUE</div>
                                    </div>
                                    <div className="border border-white/10 bg-[#0a0a0a] p-5" data-testid="purchase-tile-count">
                                        <div className="text-3xl font-display text-white">{p.count ?? 0}</div>
                                        <div className="text-[10px] font-stencil tracking-[0.25em] text-white/45 mt-1">TOTAL BUYERS</div>
                                    </div>
                                    {Object.entries(p.by_course || {}).slice(0, 2).map(([label, v]) => (
                                        <div key={label} className="border border-white/10 bg-[#0a0a0a] p-5" data-testid={`purchase-tile-course-${label.split(" ")[1] || label}`}>
                                            <div className="text-3xl font-display text-red-500">{v.count}</div>
                                            <div className="text-xs text-white/65 mt-1">{label}</div>
                                            <div className="text-[10px] font-stencil tracking-widest text-white/40 mt-1">${v.revenue.toLocaleString(undefined, {minimumFractionDigits: 2})}</div>
                                        </div>
                                    ))}
                                </div>
                                <div className="flex flex-col gap-2 items-end">
                                    <button
                                        onClick={syncStripe}
                                        className="btn-primary text-xs whitespace-nowrap"
                                        style={{ background: "rgb(220 38 38)" }}
                                        data-testid="purchase-sync-stripe-btn"
                                        title="Scan the last 30 days of Stripe sessions and pull in any missing purchases"
                                    >
                                        🔄 Sync from Stripe
                                    </button>
                                    <button
                                        onClick={sendTestAlert}
                                        className="btn-ghost text-xs whitespace-nowrap"
                                        data-testid="purchase-test-alert-btn"
                                        title="Send yourself a test purchase notification email — verify alerts work before a real sale"
                                    >
                                        📧 Send me a test alert
                                    </button>
                                    <button
                                        onClick={runEnrich}
                                        className="btn-ghost text-xs whitespace-nowrap"
                                        data-testid="purchase-enrich-btn"
                                        title="Pull email, name, phone, address from Stripe for any purchase that's missing details"
                                    >
                                        Pull buyer info from Stripe
                                    </button>
                                </div>
                            </div>

                            <p className="text-xs text-white/50 font-stencil tracking-widest mb-4">
                                [ ALL PAID PURCHASES · MOST RECENT FIRST · CLICK A ROW TO EXPAND ]
                            </p>

                            {rows.length === 0 && (
                                <div className="text-white/40 py-10 text-center font-stencil text-xs tracking-widest border border-dashed border-white/10">
                                    — No paid purchases yet. Buyers will appear here as soon as Stripe confirms payment. —
                                </div>
                            )}

                            {rows.length > 0 && (
                                <div className="space-y-3" data-testid="purchases-table">
                                    {rows.map((r) => {
                                        const full = r.full_name || [r.first_name, r.last_name].filter(Boolean).join(" ");
                                        const isOpen = openPurchase === (r.session_id || r.id);
                                        return (
                                            <div
                                                key={r.session_id || r.id}
                                                className="border border-white/10 bg-[#0a0a0a] hover:bg-white/[0.02] transition-colors cursor-pointer"
                                                onClick={() => setOpenPurchase(isOpen ? null : (r.session_id || r.id))}
                                                data-testid={`purchase-row-${r.email}`}
                                            >
                                                {/* Summary line — always visible */}
                                                <div className="grid grid-cols-12 gap-3 items-center px-4 py-4">
                                                    <div className="col-span-12 md:col-span-3">
                                                        <div className="text-white text-sm truncate" title={r.email}>{r.email || <span className="text-white/40">— no email —</span>}</div>
                                                        {full && <div className="text-white/60 text-xs mt-1">{full}</div>}
                                                    </div>
                                                    <div className="col-span-6 md:col-span-2 text-white/75 text-sm">{r.course_label}</div>
                                                    <div className="col-span-3 md:col-span-1 font-display text-emerald-400 text-right md:text-left">
                                                        ${r.amount.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                                                    </div>
                                                    <div className="col-span-9 md:col-span-3 text-white/55 text-xs">
                                                        {r.updated_at ? new Date(r.updated_at).toLocaleString() : "—"}
                                                    </div>
                                                    {/* Quick actions: text + email */}
                                                    <div className="col-span-9 md:col-span-2 flex items-center gap-2">
                                                        {r.phone ? (
                                                            <a
                                                                href={`sms:${r.phone}`}
                                                                onClick={(e) => e.stopPropagation()}
                                                                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 bg-emerald-600/20 hover:bg-emerald-600/40 border border-emerald-500/40 text-emerald-300 text-[10px] font-stencil tracking-widest transition-colors"
                                                                title={`Text ${r.phone}`}
                                                                data-testid={`purchase-sms-${r.email}`}
                                                            >
                                                                <MessageSquareText size={11} /> TEXT
                                                            </a>
                                                        ) : (
                                                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1.5 border border-white/10 text-white/30 text-[10px] font-stencil tracking-widest" title="No phone on file">
                                                                <MessageSquareText size={11} /> —
                                                            </span>
                                                        )}
                                                        {r.email && (
                                                            <a
                                                                href={`mailto:${r.email}`}
                                                                onClick={(e) => e.stopPropagation()}
                                                                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 bg-red-700/20 hover:bg-red-700/40 border border-red-500/40 text-red-300 text-[10px] font-stencil tracking-widest transition-colors"
                                                                title={`Email ${r.email}`}
                                                                data-testid={`purchase-email-${r.email}`}
                                                            >
                                                                <Mail size={11} /> EMAIL
                                                            </a>
                                                        )}
                                                    </div>
                                                    <div className="col-span-3 md:col-span-1 text-right">
                                                        {r.access_email_sent_at
                                                            ? <span className="text-emerald-500 text-[10px] font-stencil tracking-widest">EMAILED</span>
                                                            : <span className="text-yellow-500 text-[10px] font-stencil tracking-widest">PENDING</span>}
                                                    </div>
                                                </div>

                                                {/* Expanded detail */}
                                                {isOpen && (
                                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 border-t border-white/10 px-4 py-5 text-sm" onClick={(e) => e.stopPropagation()}>
                                                        <DetailField label="EMAIL" value={r.email} copy />
                                                        <DetailField label="NAME" value={full} />
                                                        <DetailField label="PHONE" value={r.phone} copy />
                                                        <DetailField label="COUNTRY" value={r.country} />
                                                        <DetailField label="POSTAL CODE" value={r.postal_code} />
                                                        <DetailField label="CITY" value={r.city} />
                                                        <DetailField label="STATE" value={r.state} />
                                                        <DetailField label="CURRENCY" value={r.currency} />
                                                        <DetailField label="SOURCE" value={`${r.source}${r.livemode ? "" : " · TEST"}`} />
                                                        <DetailField label="STRIPE SESSION" value={r.session_id} copy mono />
                                                        <DetailField label="PAYMENT INTENT" value={r.payment_id} copy mono />
                                                        <DetailField label="ACCESS EMAIL SENT" value={r.access_email_sent_at ? new Date(r.access_email_sent_at).toLocaleString() : "not sent"} />
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    );
                })()}

                {/* Course waitlist view */}
                {tab === "course_waitlist" && (
                    <div className="py-8" data-testid="tab-content-course_waitlist">
                        <p className="text-xs text-white/50 font-stencil tracking-widest mb-6">
                            [ THE BATTLEFIELD COURSE WAITLIST · NEWEST FIRST ]
                        </p>
                        {!data.course_waitlist && <div className="text-white/40 font-stencil text-xs tracking-widest">LOADING...</div>}
                        {data.course_waitlist && data.course_waitlist.length === 0 && (
                            <div className="text-white/40 py-10 text-center font-stencil text-xs tracking-widest">— No signups yet —</div>
                        )}
                        {data.course_waitlist && data.course_waitlist.length > 0 && (
                            <div className="space-y-3" data-testid="waitlist-rows">
                                {data.course_waitlist.map((w) => (
                                    <div key={w.id} className="border border-white/10 bg-[#0a0a0a] p-5" data-testid={`waitlist-row-${w.id}`}>
                                        <div className="flex flex-wrap items-baseline gap-4 mb-2">
                                            <div className="font-display text-lg">{[w.first_name, w.last_name].filter(Boolean).join(" ") || "—"}</div>
                                            <div className="text-sm text-red-500">{w.email}</div>
                                            <div className="ml-auto text-xs font-stencil tracking-widest text-white/40">{new Date(w.created_at).toLocaleString()}</div>
                                        </div>
                                        {w.reason && (
                                            <div className="mt-3 border-l-2 border-red-700/50 pl-4 py-1 text-sm text-white/75 italic leading-relaxed">
                                                "{w.reason}"
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {/* Drip preview view */}
                {tab === "drip_preview" && (
                    <DripPreview data={data.drip_preview} />
                )}

                {/* Drip activity (live diagnostics) */}
                {tab === "drip_activity" && (
                    <DripActivity data={data.drip_activity} onRetry={retryFailedDrips} retrying={retryingDrip} onPurgeTest={purgeTestData} />
                )}

                {/* Broadcasts history view */}
                {tab === "broadcasts" && (
                    <div className="py-8" data-testid="tab-content-broadcasts">
                        <div className="flex items-center justify-between mb-6">
                            <p className="text-xs text-white/50 font-stencil tracking-widest">
                                [ EVERY BROADCAST YOU'VE SENT · NEWEST FIRST ]
                            </p>
                            <button
                                onClick={refreshBroadcasts}
                                className="text-[10px] font-stencil tracking-widest text-white/40 hover:text-red-600 transition-colors"
                                data-testid="broadcasts-refresh"
                            >
                                ↻ REFRESH
                            </button>
                        </div>
                        {!data.broadcasts && <div className="text-white/40 font-stencil text-xs tracking-widest">LOADING...</div>}
                        {data.broadcasts && data.broadcasts.length === 0 && (
                            <div className="text-white/40 py-10 text-center font-stencil text-xs tracking-widest">— No broadcasts sent yet —</div>
                        )}
                        {data.broadcasts && data.broadcasts.length > 0 && (
                            <div className="space-y-2">
                                {data.broadcasts.map((b) => {
                                    const total = (b.sent ?? 0) + (b.failed ?? 0);
                                    const successRate = total > 0 ? Math.round(((b.sent ?? 0) / total) * 100) : 0;
                                    const hasErrors = (b.failed ?? 0) > 0;
                                    const isExpanded = expandedJob === b.id;
                                    return (
                                        <div
                                            key={b.id}
                                            className={`border ${hasErrors ? "border-red-700/50" : "border-white/10"} bg-[#0a0a0a]`}
                                            data-testid={`broadcast-row-${b.id}`}
                                        >
                                            <button
                                                onClick={() => setExpandedJob(isExpanded ? null : b.id)}
                                                className="w-full px-5 py-4 flex items-center gap-4 hover:bg-black transition-colors text-left"
                                            >
                                                {isExpanded ? <ChevronDown size={16} className="text-white/40 flex-shrink-0" /> : <ChevronRight size={16} className="text-white/40 flex-shrink-0" />}
                                                <div className="flex-1 min-w-0">
                                                    <div className="font-display text-base truncate">{b.subject}</div>
                                                    <div className="text-xs text-white/40 mt-1">
                                                        {new Date(b.created_at).toLocaleString()}
                                                        {b.status === "dispatching" && <span className="ml-3 text-yellow-500 animate-pulse">· DISPATCHING…</span>}
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-5 flex-shrink-0 text-xs">
                                                    <div className="flex items-center gap-1.5" title="Delivered to Resend">
                                                        <CheckCircle2 size={14} className="text-green-500" />
                                                        <span className="font-display text-lg text-green-500">{b.sent ?? 0}</span>
                                                    </div>
                                                    <div className={`flex items-center gap-1.5 ${hasErrors ? "" : "opacity-30"}`} title="Rejected by Resend">
                                                        <AlertTriangle size={14} className="text-red-600" />
                                                        <span className="font-display text-lg text-red-600">{b.failed ?? 0}</span>
                                                    </div>
                                                    <div className="font-stencil text-[10px] tracking-widest text-white/50 hidden sm:block">
                                                        {successRate}% OK
                                                    </div>
                                                </div>
                                            </button>
                                            {isExpanded && (
                                                <div className="border-t border-white/10 px-5 py-5 bg-black space-y-4 text-sm" data-testid={`broadcast-detail-${b.id}`}>
                                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                                        <div>
                                                            <div className="font-stencil text-[10px] tracking-widest text-white/40">RECIPIENTS</div>
                                                            <div className="font-display text-xl mt-1">{b.recipient_count ?? 0}</div>
                                                        </div>
                                                        <div>
                                                            <div className="font-stencil text-[10px] tracking-widest text-white/40">UNSUBSCRIBED</div>
                                                            <div className="font-display text-xl mt-1">{b.unsubscribed_excluded ?? 0}</div>
                                                        </div>
                                                        <div>
                                                            <div className="font-stencil text-[10px] tracking-widest text-green-500">DELIVERED</div>
                                                            <div className="font-display text-xl text-green-500 mt-1">{b.sent ?? 0}</div>
                                                        </div>
                                                        <div>
                                                            <div className="font-stencil text-[10px] tracking-widest text-red-600">FAILED</div>
                                                            <div className="font-display text-xl text-red-600 mt-1">{b.failed ?? 0}</div>
                                                        </div>
                                                    </div>

                                                    {b.breakdown && (
                                                        <div>
                                                            <div className="font-stencil text-[10px] tracking-widest text-white/40 mb-2">SOURCES</div>
                                                            <div className="flex flex-wrap gap-2">
                                                                {Object.entries(b.breakdown).map(([src, n]) => (
                                                                    <div key={src} className="border border-white/10 px-3 py-1 text-xs">
                                                                        <span className="font-stencil text-red-600 tracking-widest">{src.toUpperCase()}</span>
                                                                        <span className="ml-2 text-white/70">{n}</span>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    )}

                                                    {b.errors && b.errors.length > 0 && (
                                                        <div>
                                                            <div className="font-stencil text-[10px] tracking-widest text-red-600 mb-2 flex items-center gap-2">
                                                                <AlertTriangle size={12} /> SAMPLE ERRORS (FIRST 5 FAILURES)
                                                            </div>
                                                            <div className="border border-red-700/30 bg-[#1a0606] divide-y divide-red-700/20">
                                                                {b.errors.map((er, i) => (
                                                                    <div key={i} className="px-4 py-3 text-xs" data-testid={`broadcast-error-${b.id}-${i}`}>
                                                                        <div className="text-red-500">{er.email}</div>
                                                                        <div className="text-white/60 mt-1 font-mono break-all">{er.error}</div>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                            <div className="text-[10px] text-white/30 mt-2 italic">
                                                                Tip: If you see "daily_quota_exceeded" or similar — your Resend account is throttled (warmup or plan limit). Check resend.com → Settings → Billing.
                                                            </div>
                                                        </div>
                                                    )}

                                                    {!hasErrors && b.status === "complete" && (
                                                        <div className="text-xs text-green-500 font-stencil tracking-widest">
                                                            ✓ ALL RECIPIENTS DELIVERED CLEAN
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                )}

                <div className={`py-8 ${tab === "broadcasts" || tab === "drip_preview" || tab === "drip_activity" || tab === "course_waitlist" ? "hidden" : ""}`} data-testid={`tab-content-${tab}`}>
                    {!data[tab] && <div className="text-white/40 font-stencil text-xs tracking-widest">LOADING...</div>}
                    {data[tab] && rows.length === 0 && (
                        <div className="text-white/40 py-10 text-center font-stencil text-xs tracking-widest">— No records yet —</div>
                    )}
                    {data[tab] && rows.length > 0 && (
                        <div className="border border-white/10 overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="border-b border-white/10 bg-[#0a0a0a]">
                                        {tab === "contacts" && <>
                                            <th className="px-4 py-3 text-left font-stencil text-xs tracking-widest text-red-600">Name</th>
                                            <th className="px-4 py-3 text-left font-stencil text-xs tracking-widest text-red-600">Email</th>
                                            <th className="px-4 py-3 text-left font-stencil text-xs tracking-widest text-red-600">Message</th>
                                            <th className="px-4 py-3 text-left font-stencil text-xs tracking-widest text-red-600">When</th>
                                        </>}
                                        {tab === "newsletter" && <>
                                            <th className="px-4 py-3 text-left font-stencil text-xs tracking-widest text-red-600">Name</th>
                                            <th className="px-4 py-3 text-left font-stencil text-xs tracking-widest text-red-600">Email</th>
                                            <th className="px-4 py-3 text-left font-stencil text-xs tracking-widest text-red-600">Source</th>
                                            <th className="px-4 py-3 text-left font-stencil text-xs tracking-widest text-red-600">When</th>
                                        </>}
                                        {tab === "freebies" && <>
                                            <th className="px-4 py-3 text-left font-stencil text-xs tracking-widest text-red-600">Name</th>
                                            <th className="px-4 py-3 text-left font-stencil text-xs tracking-widest text-red-600">Email</th>
                                            <th className="px-4 py-3 text-left font-stencil text-xs tracking-widest text-red-600">Freebie</th>
                                            <th className="px-4 py-3 text-left font-stencil text-xs tracking-widest text-red-600">When</th>
                                        </>}
                                        {tab === "quiz" && <>
                                            <th className="px-4 py-3 text-left font-stencil text-xs tracking-widest text-red-600">Email</th>
                                            <th className="px-4 py-3 text-left font-stencil text-xs tracking-widest text-red-600">Score</th>
                                            <th className="px-4 py-3 text-left font-stencil text-xs tracking-widest text-red-600">Verdict</th>
                                            <th className="px-4 py-3 text-left font-stencil text-xs tracking-widest text-red-600">Weakest</th>
                                            <th className="px-4 py-3 text-left font-stencil text-xs tracking-widest text-red-600">When</th>
                                        </>}
                                        {tab === "checkins" && <>
                                            <th className="px-4 py-3 text-left font-stencil text-xs tracking-widest text-red-600">Email</th>
                                            <th className="px-4 py-3 text-left font-stencil text-xs tracking-widest text-red-600">Session</th>
                                            <th className="px-4 py-3 text-left font-stencil text-xs tracking-widest text-red-600">Total Days</th>
                                            <th className="px-4 py-3 text-left font-stencil text-xs tracking-widest text-red-600">Last Active</th>
                                        </>}
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map((r, i) => (
                                        <tr key={r.id || i} className="border-b border-white/5 hover:bg-[#0a0a0a]">
                                            {tab === "contacts" && <>
                                                <td className="px-4 py-3">{r.name}</td>
                                                <td className="px-4 py-3 text-red-600">{r.email}</td>
                                                <td className="px-4 py-3 max-w-md text-white/70">{r.message}</td>
                                                <td className="px-4 py-3 text-white/40 text-xs">{new Date(r.created_at).toLocaleString()}</td>
                                            </>}
                                            {tab === "newsletter" && <>
                                                <td className="px-4 py-3 text-white/80">{[r.first_name, r.last_name].filter(Boolean).join(" ") || "—"}</td>
                                                <td className="px-4 py-3 text-red-600">{r.email}</td>
                                                <td className="px-4 py-3 text-white/70 text-xs font-stencil tracking-widest">{r.source || "—"}</td>
                                                <td className="px-4 py-3 text-white/40 text-xs">{new Date(r.created_at).toLocaleString()}</td>
                                            </>}
                                            {tab === "freebies" && <>
                                                <td className="px-4 py-3 text-white/80">{[r.first_name, r.last_name].filter(Boolean).join(" ") || "—"}</td>
                                                <td className="px-4 py-3 text-red-600">{r.email}</td>
                                                <td className="px-4 py-3">{r.freebie_title}</td>
                                                <td className="px-4 py-3 text-white/40 text-xs" data-testid={`freebie-when-${i}`}>
                                                    {new Date(r.last_claimed_at || r.created_at).toLocaleString()}
                                                    {r.claim_count > 1 && <span className="ml-2 text-amber-400 font-stencil tracking-widest">×{r.claim_count}</span>}
                                                </td>
                                            </>}
                                            {tab === "quiz" && <>
                                                <td className="px-4 py-3 text-red-600">{r.email || "—"}</td>
                                                <td className="px-4 py-3 font-display text-lg">{r.result?.overall_percent}%</td>
                                                <td className="px-4 py-3 text-white/80">{r.result?.verdict}</td>
                                                <td className="px-4 py-3 font-stencil text-xs text-red-600 tracking-widest">{r.result?.weakest_pillar}</td>
                                                <td className="px-4 py-3 text-white/40 text-xs">{new Date(r.created_at).toLocaleString()}</td>
                                            </>}
                                            {tab === "checkins" && <>
                                                <td className="px-4 py-3 text-red-600">{r.email || "—"}</td>
                                                <td className="px-4 py-3 text-white/50 text-xs font-stencil">{r.session_id.slice(0, 16)}…</td>
                                                <td className="px-4 py-3 font-display text-lg">{r.total_days}</td>
                                                <td className="px-4 py-3 text-white/70">{r.last_date}</td>
                                            </>}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AdminDashboard;
